import { LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http';
import localePL from '@angular/common/locales/pl';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NoPreloading, RouterModule, TitleStrategy } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import * as fromAuth from 'src/app/core/auth/auth.reducer';
import { environment } from 'src/environments/environment';
import { APP_ROUTING } from './app-routing.const';
import { AppComponent } from './app.component';
import { AuthEffects } from './core/auth/auth.effects';
import { CoreModule } from './core/core.module';
import { LayoutModule } from './layout/layout.module';
import { RouteEventsService } from './shared/services/route-events-service/route-events.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslationModalComponent } from './shared/components/modal/translation-modal/translation-modal.component';
import { AppLanguage } from './app-language.enum';
import { LoadersComponent } from './layout/loaders/loaders.component';
import { InfoBarComponent } from './layout/info-bar/info-bar/info-bar.component';
import { SafeHtmlPipe } from './shared/pipes/safe-html.pipe';
import { GhostButtonComponent } from './shared/components/ghost-button/ghost-button.component';
import { NgCloudflareTurnstile } from '@pangz/ng-cloudflare-turnstile';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { AppTitleStrategy } from './core/services/app-title.strategy';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '/common.json' },
    { prefix: './assets/i18n/', suffix: '/tachograph.json' },
    { prefix: './assets/i18n/', suffix: '/dashboard.json' },
    { prefix: './assets/i18n/', suffix: '/driver-license.json' },
    { prefix: './assets/i18n/', suffix: '/payments.json' },
    { prefix: './assets/i18n/', suffix: '/pages.json' },
    { prefix: './assets/i18n/', suffix: '/modals.json' },
    { prefix: './assets/i18n/', suffix: '/adr.json' },
    { prefix: './assets/i18n/', suffix: '/other.json' },
    { prefix: './assets/i18n/', suffix: '/auth.json' },
    { prefix: './assets/i18n/', suffix: '/vehicle-registration.json' }
  ]);
}

export function appInitializerFactory(translate: TranslateService, injector: Injector) {
  return () =>
    new Promise<any>((resolve: any) => {
      const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
      locationInitialized.then(() => {
        const langToSet = AppLanguage.POLISH;
        translate.use(langToSet).subscribe(
          () => {
            console.log(`Successfully initialized '${langToSet}' language.'`);
          },
          () => {
            console.error(`Problem with '${langToSet}' language initialization.'`);
          },
          () => {
            resolve(null);
          }
        );
      });
    });
}

registerLocaleData(localePL);

@NgModule({
  declarations: [AppComponent, TranslationModalComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AngularSvgIconModule.forRoot(),
    ToastrModule.forRoot({
      preventDuplicates: true,
      timeOut: 3000,
      positionClass: 'toast-top-right',
      resetTimeoutOnDuplicate: true
    }),
    // NOTE: [RK] Preloading all modules disabled to check UX with on demand loading modules
    RouterModule.forRoot(APP_ROUTING, {
      onSameUrlNavigation: 'reload',
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: NoPreloading,
      anchorScrolling: 'enabled',
      enableTracing: !environment.production && JSON.parse(localStorage.getItem('DEBUG_TRACING')) === true
    }),
    StoreModule.forRoot({ auth: fromAuth.authReducer }),
    EffectsModule.forRoot([AuthEffects]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend]
      }
    }),
    CoreModule,
    InfoBarComponent,
    LayoutModule,
    LoadersComponent,
    SafeHtmlPipe,
    GhostButtonComponent,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgCloudflareTurnstile
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, Injector],
      multi: true
    },
    RouteEventsService,
    { provide: TitleStrategy, useClass: AppTitleStrategy },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
